import { MouseEvent } from 'react';
import { animated, useSpring } from 'react-spring';
import { formatDate } from '../../GlobalFunctions';
import SpinnerOverlay from '../../SpinnerOverlay';
import Table from '../Tables/Table';
import { EmptyBlock, NavArrowLeft, NavArrowRight } from './NavArrow';
import cx from 'classnames';
import styles from './TableSegment.module.scss';

interface Props {
  apiConfig: object;
  currentDate: Date;
  dataGenerated: boolean;
  goToNextDate: () => void;
  goToPreviousDate: () => void;
  isHideNames: boolean;
  isMoodConsecutive: (entry: any) => boolean;
  onEnterWarningBadge: (event: MouseEvent) => void;
  onLeaveWarningBadge: (event: MouseEvent) => void;
  onClickStudentName: (entry: any) => void;
  tableData: string[];
  showNextDateNav: boolean;
  showPreviousDateNav: boolean;
}

interface MoodMapType {
  [key: string]: string;
}

const TableSegment = ({
  apiConfig,
  currentDate,
  dataGenerated,
  goToNextDate,
  goToPreviousDate,
  isHideNames,
  onEnterWarningBadge,
  onLeaveWarningBadge,
  onClickStudentName,
  isMoodConsecutive,
  tableData,
  showNextDateNav,
  showPreviousDateNav,
}: Props) => {
  const moodMap: MoodMapType = {
    Angry: 'negative',
    Calm: 'between neutral and positive',
    Excited: 'positive',
    Happy: 'positive',
    Nervous: 'negative',
    'No check-in': 'neutral',
    Sad: 'negative',
  };

  const currentDayOfWeek = currentDate.getDay();

  const [props, api] = useSpring(
    () => ({
      config: { mass: 0.3, tension: 300, friction: 10, clamp: true },
      from: { transform: 'translate3d(0%, 0, 0)', opacity: 1 },
    }),
    [],
  );

  const handleAnimation = () => {
    api.start(apiConfig);
  };

  const onClickLeft = () => {
    handleAnimation();
    goToPreviousDate();
  };

  const onClickRight = () => {
    handleAnimation();
    goToNextDate();
  };

  const getTableHeader = () => {
    return (
      <tr>
        <th>
          <h4 className={styles.tableHeaderCell}>Full Name</h4>
        </th>
        <th>
          <div className={styles.dateCell}>
            {showPreviousDateNav ? <NavArrowLeft goToPrevious={onClickLeft} /> : <EmptyBlock />}
            <div className={styles.hideOverflow}>
              <animated.div className={styles.days} style={props}>
                <h4 className={styles.tableHeaderCell}>{formatDate(currentDate)}</h4>
              </animated.div>
            </div>
            {showNextDateNav ? <NavArrowRight goToNext={onClickRight} /> : <EmptyBlock />}
          </div>
        </th>
      </tr>
    );
  };

  return (
    <section className={styles.tableSegment}>
      {dataGenerated && tableData ? (
        <Table getTableHeader={getTableHeader}>
          {tableData.map((entry: any) => {
            const moodCheckInData = entry[`day${currentDayOfWeek}`][0];
            return (
              <tr key={entry.userIdHash}>
                <td>
                  <div className={styles.studentCell}>
                    {isHideNames ? (
                      <div className={styles.studentName}>Private</div>
                    ) : (
                      <div className={styles.studentName} onClick={() => onClickStudentName(entry)}>
                        {entry.studentName}
                      </div>
                    )}
                    {isMoodConsecutive(entry) ? (
                      <div
                        className={styles.warningBadge}
                        onMouseEnter={(event) => onEnterWarningBadge(event)}
                        onMouseLeave={(event) => onLeaveWarningBadge(event)}
                      >
                        <i className='fa fa-exclamation' />
                      </div>
                    ) : null}
                  </div>
                </td>
                <td className={styles.checkInDataCell}>
                  <p
                    className={cx(styles.data, {
                      [`${styles.neutral}`]: moodMap[moodCheckInData] === 'neutral',
                      [`${styles.betweenNeutralAndPositive}`]:
                        moodMap[moodCheckInData] === 'between neutral and positive',
                      [`${styles.positive}`]: moodMap[moodCheckInData] === 'positive',
                      [`${styles.negative}`]: moodMap[moodCheckInData] === 'negative',
                    })}
                  >
                    {moodCheckInData}
                  </p>
                </td>
              </tr>
            );
          })}
        </Table>
      ) : (
        <div className={styles.noTableData}>
          {dataGenerated ? (
            <div className={styles.emptyEntry}>There is no data to display for 'this date'.</div>
          ) : (
            <SpinnerOverlay isShow={!dataGenerated} />
          )}
        </div>
      )}
    </section>
  );
};

export default TableSegment;
